import React, { useCallback, useEffect, useRef, useState } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Table,
  message
} from "antd";
import NutritionDrawer, { DrawerFooter } from "@components/nutrition/nutrition-drawer";
import { fetchSprTov } from "@components/health/clients/health-clients-api";
import { pick, toNumber } from "@components/health/clients/health-clients-utils";
import {
  createSprKompl,
  createSprKomplItem,
  deleteSprKomplItem,
  fetchSprKompl,
  fetchSprKomplItems,
  saveSprKompl,
  saveSprKomplItem
} from "./spr-kompl-api";
import "./spr-kompl.css";

const DRAWER_RESIZE = { storageKey: "planOnline.sprKompl.cardDrawer.width", min: 480, max: 1000 };

const normalizeProductOptions = (data) => {
  const rows = Array.isArray(data) ? data : pick(data, "items", "Items") || [];
  return rows
    .map((row) => ({
      value: toNumber(pick(row, "idTov", "IdTov", "id", "Id")),
      label: pick(row, "name", "Name", "productName", "ProductName") ?? ""
    }))
    .filter((row) => row.value != null);
};

const normalizeItem = (row) => ({
  idKompl: toNumber(pick(row, "idKompl", "IdKompl")),
  idStr: toNumber(pick(row, "idStr", "IdStr")),
  idTov: toNumber(pick(row, "idTov", "IdTov")),
  tovName: pick(row, "tovName", "TovName") ?? "",
  funit: pick(row, "funit", "Funit") ?? "",
  qty: toNumber(pick(row, "qty", "Qty")) ?? 0
});

const SprKomplDrawer = ({ open, idKompl, isNew, onClose, onSaved }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [addTov, setAddTov] = useState();
  const [addQty, setAddQty] = useState(1);
  const [addSaving, setAddSaving] = useState(false);
  const itemsReqRef = useRef(0);
  const productTimerRef = useRef();
  const productRequestRef = useRef(0);

  const editing = !isNew && idKompl != null;

  useEffect(() => {
    if (!open) return;
    setAddTov(undefined);
    setAddQty(1);
    setItems([]);
    if (isNew || idKompl == null) {
      form.resetFields();
      return;
    }
    fetchSprKompl(idKompl)
      .then((data) => {
        form.setFieldsValue({
          name: pick(data, "name", "Name") ?? "",
          komment: pick(data, "komment", "Komment") ?? ""
        });
      })
      .catch((e) => messageApi.error(e.message || "Не удалось загрузить комплект"));
  }, [open, isNew, idKompl, form, messageApi]);

  const loadItems = useCallback(() => {
    if (!editing) return Promise.resolve();
    const requestId = ++itemsReqRef.current;
    setItemsLoading(true);
    return fetchSprKomplItems(idKompl)
      .then((data) => {
        if (requestId !== itemsReqRef.current) return;
        setItems(
          Array.isArray(data) ? data.map(normalizeItem).filter((r) => r.idStr != null) : []
        );
      })
      .catch((e) => {
        if (requestId !== itemsReqRef.current) return;
        messageApi.error(e.message || "Не удалось загрузить состав");
      })
      .finally(() => {
        if (requestId === itemsReqRef.current) setItemsLoading(false);
      });
  }, [editing, idKompl, messageApi]);

  useEffect(() => {
    if (!open || !editing) return;
    loadItems();
  }, [open, editing, loadItems]);

  const loadProducts = (query = "") => {
    clearTimeout(productTimerRef.current);
    const requestId = ++productRequestRef.current;
    productTimerRef.current = setTimeout(() => {
      fetchSprTov(query)
        .then((data) => {
          if (requestId !== productRequestRef.current) return;
          const next = normalizeProductOptions(data);
          setProducts((previous) => {
            const selected = previous.find((item) => item.value === addTov);
            return selected && !next.some((item) => item.value === addTov) ? [selected, ...next] : next;
          });
        })
        .catch(() => undefined);
    }, 300);
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const bodyData = {
        name: (values.name || "").trim(),
        komment: (values.komment || "").trim()
      };
      if (isNew) {
        const created = await createSprKompl(bodyData);
        messageApi.success("Комплект создан");
        onSaved?.(created);
      } else {
        const saved = await saveSprKompl(idKompl, bodyData);
        messageApi.success("Сохранено");
        onSaved?.(saved);
        onClose?.();
      }
    } catch (e) {
      if (e?.errorFields) return;
      messageApi.error(e?.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const commitQty = async (record, rawValue) => {
    const qty = toNumber(rawValue);
    if (qty == null || qty <= 0) {
      messageApi.warning("Количество должно быть больше нуля");
      loadItems();
      return;
    }
    if (qty === record.qty) return;
    try {
      await saveSprKomplItem(idKompl, record.idStr, { idTov: record.idTov, qty });
      setItems((prev) =>
        prev.map((row) => (row.idStr === record.idStr ? { ...row, qty } : row))
      );
    } catch (e) {
      messageApi.error(e?.message || "Не удалось сохранить количество");
      loadItems();
    }
  };

  const removeItem = async (record) => {
    try {
      await deleteSprKomplItem(idKompl, record.idStr);
      loadItems();
    } catch (e) {
      messageApi.error(e?.message || "Не удалось удалить строку");
    }
  };

  const addItem = async () => {
    if (addTov == null) return;
    setAddSaving(true);
    try {
      await createSprKomplItem(idKompl, { idTov: addTov, qty: addQty ?? 1 });
      setAddTov(undefined);
      setAddQty(1);
      loadItems();
    } catch (e) {
      messageApi.error(e?.message || "Не удалось добавить позицию");
    } finally {
      setAddSaving(false);
    }
  };

  const itemColumns = [
    { title: "Товар", dataIndex: "tovName", key: "tovName", ellipsis: true },
    { title: "Ед.", dataIndex: "funit", key: "funit", width: 60 },
    {
      title: "Кол-во",
      dataIndex: "qty",
      key: "qty",
      width: 120,
      align: "right",
      render: (qty, record) => (
        <InputNumber
          size="small"
          min={0.001}
          step={1}
          defaultValue={qty}
          onBlur={(e) => commitQty(record, e.target.value)}
          onPressEnter={(e) => commitQty(record, e.target.value)}
        />
      )
    },
    {
      title: "",
      key: "del",
      width: 40,
      render: (_, record) => (
        <Popconfirm title="Удалить строку?" onConfirm={() => removeItem(record)}>
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Popconfirm>
      )
    }
  ];

  return (
    <NutritionDrawer
      open={open}
      title={isNew ? "Новый комплект" : "Комплект"}
      width={640}
      resizable={DRAWER_RESIZE}
      onClose={onClose}
    >
      {contextHolder}
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Наименование"
          rules={[{ required: true, message: "Укажите наименование" }]}
        >
          <Input maxLength={80} />
        </Form.Item>
        <Form.Item name="komment" label="Комментарий">
          <Input maxLength={255} />
        </Form.Item>
      </Form>
      <div className="spr-kompl__section-title">Состав</div>
      {!editing ? (
        <Alert type="info" showIcon message="Сначала сохраните комплект" />
      ) : (
        <div className="spr-kompl__items">
          <Table
            size="small"
            rowKey="idStr"
            pagination={false}
            columns={itemColumns}
            dataSource={items}
            loading={itemsLoading}
            locale={{ emptyText: "Состав пуст" }}
          />
          <div className="spr-kompl__add-row">
            <Select
              allowClear
              showSearch
              filterOption={false}
              placeholder="Товар"
              options={products}
              value={addTov}
              onSearch={loadProducts}
              onDropdownVisibleChange={(visible) => visible && loadProducts("")}
              onChange={setAddTov}
              style={{ flex: 1 }}
            />
            <InputNumber
              min={0.001}
              step={1}
              value={addQty}
              onChange={(v) => setAddQty(v ?? 1)}
              style={{ width: 110 }}
            />
            <Button
              icon={<PlusOutlined />}
              disabled={addTov == null}
              loading={addSaving}
              onClick={addItem}
            >
              Добавить
            </Button>
          </div>
        </div>
      )}
      <DrawerFooter onSave={save} onClose={onClose} saving={saving} saveText="ОК" />
    </NutritionDrawer>
  );
};

export default SprKomplDrawer;
